import type { Exam, ExamState } from '../../exam.js';
import { executeQuery } from '../agents/query-agent.js';
import { TaskTypeDescription, taskTypeBehaviours, TaskType } from '../task-types/task-main.js';

type ToolHandler = (exam: Exam, toolCallArgs: any) => Promise<object>;

export const queryToolsDef = [
  {
    type: 'function',
    function: {
      name: 'query_exams',
      description:
        'Ruft detaillierte Informationen zu einer oder mehreren bestimmten Klausuren ab (anhand ihrer ID oder der aktuellen Klausur). ' +
        'Nutze dieses Tool, wenn du Aufgaben-Inhalte (Aufgabenstellung, Musterlösung, Bewertungsschema) oder den Bearbeitungsstatus einer Klausur brauchst. ' +
        'Wenn eine ID nicht gefunden wird, bekommst du eine Liste aller verfügbaren Klausuren (Name + ID) des aktuellen Nutzers zurück. ' +
        'Rufe das Tool einfach mit der gewünschten Auswahl auf – der Detailgrad passt sich automatisch der Anzahl der Klausuren an; das Ergebnis weist dich darauf hin, wenn etwas gekürzt wurde. ' +
        'So skaliert es (nur für DEINE interne Planung, um treffsichere Drilldown-Vorschläge zu machen – nenne dem Nutzer NIE diese Zahlen/Grenzen): 1 Klausur = alle angeforderten Felder; 2–3 Klausuren = nur Aufgabenstellung (ohne Musterlösung/Bewertungsschema); 4–5 Klausuren = nur Stammdaten + Status; mehr als 5 = nur Stammdaten. ' +
        'Maximal 20 Klausuren pro Aufruf; für eine reine Übersicht aller Klausuren get_available_options nutzen. ' +
        'Sprich mit dem Nutzer ausschließlich über Klausurnamen, niemals über IDs.',
      parameters: {
        type: 'object',
        properties: {
          exams: {
            type: 'array',
            maxItems: 20,
            items: {
              type: 'object',
              properties: {
                examId: {
                  type: 'string',
                  description: "Klausur-ID oder 'this' für die aktuelle Klausur im Gesprächskontext.",
                },
                taskPaths: {
                  type: 'array',
                  items: { type: 'string' },
                  description:
                    "Optionaler Aufgaben-Filter NUR für diese Klausur, als Aufgabenpfade, z.B. ['2', '2.a', '3.b.i']. Ein Pfad verbindet die Aufgabennummern der Ebenen mit Punkten (Struktur [2, a, i] → '2.a.i'); ein übergeordneter Pfad schließt seine Unteraufgaben ein. Weggelassen = alle Aufgaben dieser Klausur. Wirkt nur zusammen mit taskFields.",
                },
              },
              required: ['examId'],
            },
            description:
              "Welche Klausuren abgefragt werden sollen. Jeder Eintrag enthält die Klausur (examId oder 'this') und optional einen eigenen Aufgaben-Filter (taskPaths) für genau diese Klausur – Klausuren haben unterschiedliche Aufgaben. Leer/weggelassen = aktuelle Klausur. MAXIMAL 20 Klausuren pro Aufruf; für eine Übersicht aller Klausuren get_available_options verwenden.",
          },
          taskFields: {
            type: 'array',
            items: { type: 'string', enum: ['description', 'masterSolution', 'gradingScheme'] },
            description:
              "Welche Informationen pro Aufgabe zurückgegeben werden sollen (gilt für alle abgefragten Klausuren). 'description' = Aufgabenstellung, 'masterSolution' = Musterlösung, 'gradingScheme' = Bewertungsschema inkl. maximaler Punkte. Wähle nur, was du wirklich brauchst. Leer/weggelassen = keine Aufgaben-Details.",
          },
          examState: {
            type: 'array',
            items: {
              type: 'string',
              enum: ['workflow', 'grading', 'uncertainties'],
            },
            description:
              'Welche Status-Aspekte der Klausur als ausführliche Beschreibung zurückgegeben werden sollen. Mögliche Werte: ' +
              "'workflow' = Gesamtfortschritt (Metadaten, Upload, Aufgabenerkennung, Abgaben-Verarbeitung, Musterlösung, Bewertungsschema, Bewertung); " +
              "'grading' = Bewertungsfortschritt in Prozent, Anzahl bewerteter Aufgaben und laufende Bewertungen; " +
              "'uncertainties' = pro Aufgabe die Anzahl Unsicherheiten in den Schülerantworten UND in der Bewertung; sind Antwort-Unsicherheiten vorhanden, zusätzlich ein Hinweis, diese zuerst zu lösen (sie fließen in die Bewertung ein). " +
              'Leer/weggelassen = kein Status.',
          },
        },
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'query_submissions',
      description:
        'Ruft die Schülerabgaben einer oder mehrerer Klausuren ab – optional gefiltert nach Schülernamen und/oder bestimmten Aufgaben. ' +
        'Du kannst mehrere Klausuren und mehrere Schüler in einem einzigen Aufruf abfragen (z.B. um über mehrere Klausuren hinweg zu vergleichen). ' +
        'Du kannst wählen, ob die erkannten Schülerantworten und/oder die Bewertungsergebnisse enthalten sein sollen. ' +
        'Wird eine Klausur-ID nicht gefunden, bekommst du eine Liste der verfügbaren Klausuren; nicht gefundene Schüler werden pro Klausur mit den verfügbaren Schülernamen aufgeführt. ' +
        'Rufe das Tool einfach mit der gewünschten Auswahl auf – der Detailgrad passt sich automatisch der Datenmenge an; das Ergebnis weist dich darauf hin, wenn etwas gekürzt wurde. ' +
        'So skaliert es (nur für DEINE interne Planung, um treffsichere Spezifikationsvorschläge zu machen – nenne dem Nutzer NIE diese Zahlen/Grenzen): volle Antworten + Begründungen bei höchstens 3 Schülern ODER einer einzelnen Aufgabe (bis 100 Abgaben); nur Punkte je Aufgabe bis ca. 35 Abgaben; darüber nur Gesamtpunkte je Schüler; bei sehr vielen Abgaben über mehrere Klausuren nur ein Klausurdurchschnitt. ' +
        'Wichtig: Für eine GANZE Klausur (alle Aufgaben × alle Schüler) gibt es die Antworten/Begründungen NICHT auf einmal – schlage dem Nutzer dann natürlich eine einzelne Aufgabe (ganze Klasse) oder wenige Schüler vor, ohne Zahlen/Grenzen zu nennen. ' +
        'Über detailLevel kannst du freiwillig gröber aggregieren (z.B. nur Gesamtpunkte), um Kontext zu sparen. ' +
        'Maximal 20 Klausuren pro Aufruf. ' +
        'Sprich mit dem Nutzer ausschließlich über Klausur- und Schülernamen, niemals über IDs.',
      parameters: {
        type: 'object',
        properties: {
          exams: {
            type: 'array',
            maxItems: 20,
            items: {
              type: 'object',
              properties: {
                examId: {
                  type: 'string',
                  description: "Klausur-ID oder 'this' für die aktuelle Klausur im Gesprächskontext.",
                },
                taskPaths: {
                  type: 'array',
                  items: { type: 'string' },
                  description:
                    "Optionaler Aufgaben-Filter NUR für diese Klausur, als Aufgabenpfade, z.B. ['2', '2.a', '3.b.i']. Ein Pfad verbindet die Aufgabennummern der Ebenen mit Punkten (Struktur [2, a, i] → '2.a.i'); ein übergeordneter Pfad schließt seine Unteraufgaben ein. Weggelassen = alle Aufgaben dieser Klausur.",
                },
              },
              required: ['examId'],
            },
            description:
              "Welche Klausuren abgefragt werden sollen. Jeder Eintrag enthält die Klausur (examId oder 'this') und optional einen eigenen Aufgaben-Filter (taskPaths) für genau diese Klausur – Klausuren haben unterschiedliche Aufgaben. Leer/weggelassen = aktuelle Klausur. Das Ergebnis ist pro Klausur gruppiert. MAXIMAL 20 Klausuren pro Aufruf; für eine Übersicht aller Klausuren get_available_options verwenden.",
          },
          studentNames: {
            type: 'array',
            items: { type: 'string' },
            description:
              'Liste von Schülernamen, nach denen gefiltert werden soll (gilt für alle abgefragten Klausuren; Abgleich über den Namen). Leer/weggelassen = alle Schüler der jeweiligen Klausur.',
          },
          studentAnswer: {
            type: 'boolean',
            description:
              'Wenn true, wird die erkannte Schülerantwort pro Aufgabe zurückgegeben – vollständig aber nur bei einer einzelnen Aufgabe (ganze Klasse, bis 100 Abgaben) oder bei höchstens 3 Schülern, nicht für eine ganze Klausur über alle Aufgaben.',
          },
          gradingResult: {
            type: 'boolean',
            description:
              'Wenn true, wird das Bewertungsergebnis pro Aufgabe zurückgegeben (Punkte inkl. Maximum, Konfidenz, Begründung sowie Bestätigung/Feedback des Lehrers).',
          },
          detailLevel: {
            type: 'string',
            enum: ['full', 'task_scores', 'totals', 'exam_average'],
            description:
              'Optionaler Detailgrad-Wunsch. Normalerweise wählt das Backend den Detailgrad automatisch anhand der Datenmenge. ' +
              'Mit diesem Parameter kannst du einen GRÖBEREN (stärker aggregierten) Grad erzwingen, um Kontext zu sparen – auch bei kleinen Abfragen, z.B. wenn du nur die Gesamtpunkte oder einen Schnitt brauchst. ' +
              "'full' = Antworten + Punkte + Begründungen je Aufgabe; 'task_scores' = nur Punkte je Aufgabe; 'totals' = nur Gesamtpunkte je Schüler; 'exam_average' = nur ein Gesamtdurchschnitt je Klausur. " +
              'Einen FEINEREN Grad als die automatische Grenze kannst du NICHT erzwingen (Schutz vor zu großen Antworten) – ein solcher Wunsch wird auf den erlaubten Grad begrenzt. Leer/weggelassen = automatisch.',
          },
        },
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_available_options',
      description:
        'Erkundungs-Tool: liefert Listen von Klausuren, Schülern oder Aufgabenpfaden.' +
        'Nutze dies, um die richtigen IDs/Namen für weitere Tool-Aufrufe zu finden, falls der Nutzer nach einfach Auflistungen fragt oder um ihm die verfügbaren Optionen zu zeigen.' +
        'Setze einen oder mehrere der Parameter. Klausuren werden mit Name und ID geliefert, Schüler mit Namen, Aufgaben als Pfade (z.B. "2.a.i"). ' +
        'Wird etwas nicht gefunden, bekommst du die verfügbaren Optionen des aktuellen Nutzers zurück. ' +
        'WICHTIG: Verwende IDs nur intern für weitere Tool-Aufrufe und nenne dem Nutzer ausschließlich Klausur- und Schülernamen.',
      parameters: {
        type: 'object',
        properties: {
          allExamsForCurrentUser: {
            type: 'boolean',
            description: 'true = liste alle Klausuren des aktuellen Nutzers (Name + ID).',
          },
          allStudentsForCurrentUser: {
            type: 'boolean',
            description: 'true = liste alle Schülernamen über alle Klausuren des aktuellen Nutzers.',
          },
          studentsForKnownExams: {
            type: 'array',
            items: { type: 'string' },
            description:
              "Liste von Klausur-IDs (oder 'this' für die aktuelle Klausur): liefert pro Klausur die Schülernamen. Gib alle gewünschten Klausuren in EINEM Aufruf als Liste an.",
          },
          tasksForKnownExams: {
            type: 'array',
            items: { type: 'string' },
            description:
              "Liste von Klausur-IDs (oder 'this' für die aktuelle Klausur): liefert pro Klausur die Aufgabenpfade (z.B. '2.a.i'). Gib alle gewünschten Klausuren in EINEM Aufruf als Liste an.",
          },
          examsForKnownStudents: {
            type: 'array',
            items: { type: 'string' },
            description:
              'Liste von Schülernamen: liefert pro Name die Klausuren (Name + ID), in denen dieser Schüler eine Abgabe hat (Abgleich über den Namen). Gib alle gewünschten Schüler in EINEM Aufruf als Liste an.',
          },
        },
      },
    },
  },
];

export function handleQueryTool(name: string): ToolHandler {
  return async (exam: Exam, toolCallArgs: any) =>
    executeQuery(name, toolCallArgs ?? {}, { currentExamId: exam?._id ?? null });
}

export function queryToolAvailability(_examState: ExamState): boolean {
  return true;
}

export const taskTypeToolDef = [
  {
    type: 'function',
    function: {
      name: 'get_task_types',
      description:
        'Returns all defined task types with their descriptions and implementation status. ' +
        'Use this when the teacher asks which task types are supported or what Gradi can currently grade.',
      parameters: {
        type: 'object',
        properties: {},
      },
    },
  },
];

export function handleGetTaskTypes(_exam: Exam, _args: any): Promise<object> {
  const taskTypes = Object.entries(TaskTypeDescription).map(([key, description]) => {
    const behaviour = taskTypeBehaviours[key as TaskType];
    return {
      taskType: key,
      description,
      implemented: behaviour.implemented === true,
    };
  });
  return Promise.resolve({ taskTypes });
}

export function taskTypeToolAvailability(_examState: ExamState): boolean {
  return true;
}
